import React, { useEffect, useMemo, useState } from "react";
import { currentCompassDirection } from "@/lib/compassDirections";

/**
 * DynamicIntegrationArea — a quiet quote that changes with the active
 * Rituals sub-mode. Sits above the ClosingMoment so the page ends with a
 * ripple of the practice rather than a boundary.
 *
 * Copy is intentionally short and hand-picked; the copper accent is used
 * sparingly for a single hairline and the eyebrow label.
 */
const LINES = {
  affirmations: [
    "I trust the person I'm becoming.",
    "I am allowed to grow at my own pace.",
    "My worth is not up for negotiation.",
  ],
  intentions: [
    "Today, I choose presence over pressure.",
    "Today, I choose kindness — to myself first.",
    "Today, one small honest step is enough.",
  ],
  meditation: [
    "Inhale. Receive. Exhale. Release.",
    "The breath is always here, waiting.",
    "There is nothing to fix. Only to notice.",
  ],
  read: [
    "Sometimes becoming begins with simply being still.",
    "The quiet is not empty. It is where you hear yourself again.",
    "A gentle life is not a small life.",
  ],
};

const LABELS = {
  affirmations: "Affirmation",
  intentions:   "Intention",
  meditation:   "Breath",
  read:         "A Quiet Line",
};

export default function DynamicIntegrationArea({ mode }) {
  // When the Soul pillar is active we replace the rotating quotes with the
  // day's compass reading so the footer message stays synced to the same
  // direction the Soul Compass card is showing above.
  const compass = useMemo(() => currentCompassDirection(), []);
  const isCompass = mode === "compass";

  const list = LINES[mode] || LINES.affirmations;
  const label = isCompass ? `${compass.label} — ${compass.theme}` : (LABELS[mode] || LABELS.affirmations);

  // Pick a line by day so it stays stable within a session but rotates
  // organically day-to-day. We reset the local counter when mode changes
  // so a tab-switch feels responsive.
  const [tick, setTick] = useState(0);
  useEffect(() => { setTick(0); }, [mode]);
  const line = useMemo(() => {
    if (isCompass) return compass.footer;
    const day = new Date().getDate();
    return list[(day + tick) % list.length];
  }, [list, tick, isCompass, compass]);

  // On the Affirmations and Intentions tabs the centrepiece moment inside
  // `AffirmationsLibrary` / `DailyIntentionSelector` (plus the closing
  // "Move through your life with intention." signature on Intentions)
  // already serves this role. Rendering a second rotating quote here
  // would read as a duplicate. Guard AFTER the hooks so React's
  // hook-order invariant is preserved.
  if (mode === "affirmations" || mode === "intentions") return null;

  return (
    <section
      className="py-14 md:py-20"
      data-testid="rituals-dynamic-integration"
      aria-live="polite"
    >
      <div
        className="max-w-2xl mx-auto text-center"
        key={`${mode}-${tick}`}
        style={{ animation: "reveal 600ms ease-out both" }}
      >
        <div
          className="text-[10px] uppercase tracking-[0.32em]"
          style={{ color: "#C56B3F" }}
          data-testid="rituals-integration-label"
        >
          {label}
        </div>
        <div
          className="mt-5 mx-auto w-10 h-px"
          style={{ background: "#C56B3F" }}
          aria-hidden
        />
        <p
          className="mt-6 font-display italic text-2xl md:text-3xl lg:text-4xl leading-snug"
          style={{ color: "#292725" }}
          data-testid="rituals-integration-line"
        >
          {line}
        </p>
        <button
          type="button"
          onClick={() => setTick((t) => t + 1)}
          data-testid="rituals-integration-next"
          className="mt-6 text-[10px] uppercase tracking-[0.28em] transition-colors"
          style={{ color: "#746C63" }}
        >
          Another →
        </button>
      </div>
    </section>
  );
}
